/* eslint-disable react/display-name */
import React, { useState, forwardRef } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useRouter } from "utils";
import { makeStyles } from "@material-ui/styles";
import { ListItem, Button, Collapse } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

/* eslint-disable */
const CustomRouterLink = forwardRef((props, ref) => (
	<div ref={ref} style={{ flexGrow: 1 }}>
		<RouterLink {...props} />
	</div>
));

const useStyles = makeStyles((theme) => ({
	item: {
		display: 'flex',
		alignItems: 'center',
		padding: theme.spacing(0.75, 2),
		margin: theme.spacing(0.5, 0),
		backgroundColor: theme.palette.highlight, // Use highlight color like HomeCard
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.highlight}`, // Subtle border
		width: '100%',
		boxShadow: theme.shadows[1], // Add subtle shadow like other cards
		// Slightly narrower on extra-small screens for more side spacing
		[theme.breakpoints.down('xs')]: {
			padding: theme.spacing(1.25, 2),
			margin: theme.spacing(0.5, 3),
			width: `calc(100% - ${theme.spacing(6)}px)`,
		},
		'& .MuiListItem-button': {
			borderRadius: theme.spacing(1),
			backgroundColor: 'transparent', // Transparent to show parent background
			display: 'flex',
			alignItems: 'center',
			width: '100%',
			'&:hover': {
				backgroundColor: 'rgba(0, 0, 0, 0.04)', // Subtle hover effect
			}
		},
		'& .MuiListItemText-root': {
			'& .MuiListItemText-primary': {
				color: theme.palette.text.secondary, // Default to gray instead of blue
				fontWeight: 'bold',
				letterSpacing: 0.1,
			},
			// Remove mobile-specific font overrides to match web
		},
		'&:hover': {
			backgroundColor: theme.palette.highlight,
			boxShadow: theme.shadows[2], // Enhanced shadow on hover
		}
	},
	itemLeaf: {
		display: 'flex',
		alignItems: 'center',
		padding: theme.spacing(1, 2),
		margin: theme.spacing(0.75, 1), // Increased from 0.5 to 0.75 for even more spacing
		backgroundColor: theme.palette.highlight, // Match main item background
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.highlight}`,
		width: `calc(100% - ${theme.spacing(2)}px)`,
		boxShadow: theme.shadows[1],
		// Slightly narrower on extra-small screens for more side spacing
		[theme.breakpoints.down('xs')]: {
			padding: theme.spacing(1.5, 2),
			margin: theme.spacing(0.75, 4),
			width: `calc(100% - ${theme.spacing(8)}px)`,
		},
		'& .MuiListItem-button': {
			borderRadius: theme.spacing(1),
			backgroundColor: 'transparent',
			display: 'flex',
			alignItems: 'center',
			width: '100%',
			'&:hover': {
				backgroundColor: 'rgba(0, 0, 0, 0.04)', // Subtle hover like main
			},
			'&.active': {
				backgroundColor: 'transparent',
			}
		},
		'& .MuiListItemText-root': {
			'& .MuiListItemText-primary': {
				color: theme.palette.text.secondary, // Default to gray instead of blue
				fontWeight: 'bold',
				letterSpacing: 0.1,
			},
			// Remove mobile-specific font overrides to match web
		},
		'&:hover': {
			backgroundColor: theme.palette.highlight,
			boxShadow: theme.shadows[2],
			borderColor: theme.palette.highlight,
		}
	},
	button: {
		color: theme.palette.text.secondary, // Gray default link color
		padding: '10px 8px',
		justifyContent: 'flex-start',
		textTransform: 'none',
		letterSpacing: 0,
		width: '100%',
		[theme.breakpoints.down('xs')]: {
			padding: '14px 8px',
		},
		// Remove mobile-specific font overrides to match web
		'&:hover': {
			backgroundColor: 'white',
			color: theme.palette.text.secondary
		}
	},
	buttonLeaf: {
		color: theme.palette.text.secondary, // Gray default link color
		padding: '8px 8px',
		justifyContent: 'flex-start',
		textTransform: 'none',
		letterSpacing: 0,
		width: '100%',
		fontWeight: theme.typography.fontWeightRegular,
		[theme.breakpoints.down('xs')]: {
			padding: '12px 8px',
		},
		'&.depth-0': {
			fontWeight: theme.typography.fontWeightMedium
		},
		// Remove mobile-specific font overrides to match web
		'&:hover': {
			backgroundColor: 'white',
			color: theme.palette.text.secondary
		}
	},
    icon: {
        // Make the icon container a perfect circle
        borderRadius: '50%',
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.primary.main,
        width: 26,
        height: 26,
        minWidth: 26, // Smaller circle to better match text size
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginRight: theme.spacing(1),
        '& svg': {
            fontSize: '1.1rem !important',
            width: '1.1rem !important',
            height: '1.1rem !important'
        }
    },
	expandIcon: {
		marginLeft: 'auto',
		height: 20,
		width: 20,
		color: theme.palette.primary.main, // Dark blue color to match text
		backgroundColor: theme.palette.background.paper, // White background
		borderRadius: '50%', // Circular background
		padding: 2, // Small padding
	},
	label: {
		display: "flex",
		alignItems: "center",
		marginLeft: "auto",
	},
	active: {
		color: theme.palette.primary.main, // Keep active state highlighted in primary
		fontWeight: theme.typography.fontWeightMedium,
		"& $icon": {
			color: theme.palette.primary.main,
		},
	},
}));

const NavigationListItem = (props) => {
	const {
		title,
		href,
		depth,
		children,
		icon: Icon,
		className,
		open: openProp,
		label: Label,
		...rest
	} = props;

	const classes = useStyles(props);
	const { history } = useRouter();
	const [open, setOpen] = useState(openProp);

	const handleToggle = () => {
		setOpen((open) => !open);
		if (href) {
			history.push(href); // todo: dispatch push()
		}
	};

	let paddingLeft = 8;

	if (depth > 0) {
		// paddingLeft = 32 + 8 * depth
		paddingLeft = 10 + 8 * depth;
	}

	const style = {
		paddingLeft,
	};

	if (children) {
		return (
			<ListItem
				{...rest}
				className={clsx(classes.item, className)}
				disableGutters
			>
				<Button
					activeClassName={classes.active}
					className={classes.button}
					onClick={handleToggle}
					style={style}
				>
					{Icon && <Icon className={classes.icon} />}
					{title}
					{open ? (
						<ExpandLessIcon
							className={classes.expandIcon}
							color="inherit"
						/>
					) : (
						<ExpandMoreIcon
							className={classes.expandIcon}
							color="inherit"
						/>
					)}
				</Button>
				<Collapse in={open}>{children}</Collapse>
			</ListItem>
		);
	}

	return (
		<ListItem
			{...rest}
			className={clsx(classes.itemLeaf, className)}
			disableGutters
		>
			<Button
				activeClassName={classes.active}
				className={clsx(classes.buttonLeaf, `depth-${depth}`)}
				component={CustomRouterLink}
				exact
				style={style}
				to={href}
			>
				{Icon && <Icon className={classes.icon} />}
				{title}
				{Label && (
					<span className={classes.label}>
						<Label />
					</span>
				)}
			</Button>
		</ListItem>
	);
};

NavigationListItem.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	depth: PropTypes.number.isRequired,
	href: PropTypes.string,
	icon: PropTypes.any,
	label: PropTypes.any,
	open: PropTypes.bool,
	title: PropTypes.string.isRequired,
};

NavigationListItem.defaultProps = {
	depth: 0,
	open: false,
};

export default NavigationListItem;
/* eslint-enable */
